import React, {
    useCallback, useContext, useMemo, useEffect, useState
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { renderContentFromMetadata, validateContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication, AUTH_ERRORS } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import appConfig from 'app-config';
import AuthMessageComponent from '../AuthMessageComponent/AuthMessageComponent';
import { checkEmailValidity } from '../LoginUtil';

import metadata from './SignUpComponent.metadata.json5';
import messages from './SignUpComponent.messages';
import styles from './SignUpComponent.module.scss';

function checkPasswordValidation(password) {
    const {passwordPolicy} = appConfig;
    const passwordPolicyRegex = new RegExp(passwordPolicy)
    let isValid = true;
    isValid =  isValid ? passwordPolicyRegex.test(password) : false;
    return isValid;

}

function SignUpComponent(props) {
    const translator = useContext(TranslatorContext);
    const [pageView, setPageView] = useState();
    const [pageError, updatePageError] = useState();
    const [formData, updateFormData] = useState({});
    const { signUp, login, loginWithoutTwilo } = useAuthentication();
    const { isComponentValid, registerComponentValidation } = useValidation('SignUpComponent');
    const { history, successPath, onBackToSignIn } = props;
    const [showPassword , setShowPassword] = useState(false);
    const [showConfirmPassword , setShowConfirmPassword] = useState(false);

    const handleSignUp = useCallback(
        (evt) => {
            if (evt) {
                evt.preventDefault();
            }

            if (!isComponentValid) {
                updatePageError(AUTH_ERRORS.fieldEmpty);
                return Promise.resolve();
            }

            const email = _.get(formData, 'emailAddress');
            const password = _.get(formData, 'password');
            const confirmPassword = _.get(formData, 'confirmPassword');
            const phoneNo = _.get(formData, 'phoneNumber');

            if (!checkEmailValidity(email)) {
                updatePageError('invalidEmail');
                return Promise.resolve();
            }
            if (password !== confirmPassword) {
                updatePageError('passwordsNotMatching');
                return Promise.resolve();
            }

            const isRuleValid = checkPasswordValidation(password);
            if (!isRuleValid) {
                updatePageError('passwordRuleError');
                return Promise.resolve();
            }
            return signUp({
                givenName: _.get(formData, 'firstName'),
                familyName: _.get(formData, 'lastName'),
                userName: email,
                email: email,
                password: password,
                phoneNumber: phoneNo

            })
                .then(() => {
                    
                    loginWithoutTwilo({
                        username: email,
                        password: password
                    }).then(
                        () => {
                            // redirect user to home view
                            history.push(successPath);
                        },
                        () => {
                            setPageView(AUTH_ERRORS.autoLoginError);
                        }
                    );
                })
                .catch((err) => {
                    switch (err.error) {
                        case AUTH_ERRORS.userAlreadyExists:
                            setPageView(AUTH_ERRORS.userAlreadyExists);
                            break;
                        default:
                            setPageView(AUTH_ERRORS.invalidAttempt);
                            break;
                    }
                });
        },
        [formData, history, isComponentValid, login, signUp, successPath]
    );

    const readValue = useCallback((id, path) => _.get(formData, path), [formData]);

    const writeValue = useCallback(
        (value, path) => {
            const nextFormData = _.cloneDeep(formData);
            _.set(nextFormData, path, value);
            updateFormData(nextFormData);
        },
        [formData]
    );
	
	const getLink = useCallback(() => {
        const redirectRoute = "https://gwportal.heartlandfarmmutual.com/account-management/login-page";
        return (
            <FormattedMessage
                {...messages.existingAccountErrorMsg}
                values={{
                    here: <Link to={redirectRoute}>{translator(messages.here)}</Link>
                }}
            />
        );
    }, [translator]);

    

    const toggleShowPassword = useCallback((value)=>{
        setShowPassword(value);
    },[setShowPassword, showPassword])

    const toggleShowConfirmPassword = useCallback((value)=>{
        setShowConfirmPassword(value);
    },[setShowConfirmPassword, showConfirmPassword])
    

    const overrideProps = useMemo(
        () => ({
            '@field': {
                onValueChange: writeValue
            },
            signUpContainer: {
                visible: _.isUndefined(pageView)
            },
            errorMsg: {
                visible: !_.isUndefined(pageError),
                message: translator(messages[pageError])
            },
            emailAlreadyExistsError: {
                visible: pageView === AUTH_ERRORS.userAlreadyExists
            },
            invalidAttemptError: {
                visible: pageView === AUTH_ERRORS.invalidAttempt,
				displayBackToSignIn: false
            },
			existingAccountErrorMsg: {
                visible: pageView === AUTH_ERRORS.invalidAttempt,
                formattedDescription: getLink()                
            },
            unableToLoginAutomaticallyError: {
                visible: pageView === AUTH_ERRORS.autoLoginError
            },
            signUpButton: {
                trigger: handleSignUp
            },
            password:{
                inputType: showPassword ? "text": "password" 
            },
            confirmPassword:{
                 inputType: showConfirmPassword ? "text": "password"
            },
            showPasswordCheckBox:{
                value: showPassword,
                onValueChange: (value) => toggleShowPassword(value),
            },
            showConfirmPasswordCheckBox:{
               value:showConfirmPassword,
               onValueChange: (value) => toggleShowConfirmPassword(value),
            }
        }),
        [handleSignUp, pageError, pageView, translator, writeValue,showPassword,showConfirmPassword]
    );

    const resolvers = useMemo(
        () => ({
            resolveValue: readValue,
            resolveCallbackMap: {
                handleBackToSignIn: onBackToSignIn
            },
            resolveComponentMap: {
                authmessagecomponent: AuthMessageComponent
            },
            resolveClassNameMap: styles
        }),
        [onBackToSignIn, readValue]
    );

    const validateForm = useCallback(() => {
        return validateContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
    }, [overrideProps, resolvers]);

    useEffect(() => {
        registerComponentValidation(validateForm);
    }, [registerComponentValidation, validateForm]);

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

SignUpComponent.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    successPath: PropTypes.string.isRequired,
    onBackToSignIn: PropTypes.func
};

SignUpComponent.defaultProps = {
    onBackToSignIn: undefined
};

export default withRouter(SignUpComponent);
